<template>
  <form @submit.prevent="onSubmit">
    <div class="font-medium text-xs text-left leading-snug p-4 bg-charcoal-100 dark:bg-dark-950 space-y-4" :class="[ isFullPage ? '' : 'overflow-auto max-h-[40vh]' ]">
      <div class="flex items-center text-sm">
        <IconBase name="cute/shield-fill" :width="20" class="mr-1" />
        
        <p class="font-bold grow leading-none">
          {{ $t('components.Event.payment.secured') }}
        </p>

        <div>
          <IconBase name="square" :width="70" />
        </div>
      </div>

      <div class="bg-white dark:bg-dark-800 dark:text-dark-50 p-4 rounded-xl relative  text-light-950 shadow">
        <div class="min-h-24 bg-charcoal-100 rounded-lg relative" v-if="formData.amount <= 0">
          <div class="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2">
            <IconBase name="cute/spinner" class="spin text-charcoal-400" />
          </div>
        </div>
        <template v-else>
          <div class="space-y-6" v-show="isNewCard">
            <div class="space-y-2" :class="[ isLoading ? 'opacity-30 pointer-events-none' : '' ]">
              <p class="font-bold">{{ $t('form.adressBilling') }}</p>
              <div class="grid grid-cols-2 gap-2">
                <InputBase autocomplete="given-name" size="sm" v-model="billingDetails.firstName" :label="$t('form.name')" />
                <InputBase autocomplete="family-name" size="sm" v-model="billingDetails.lastName" :label="$t('form.lastName')" />

                <InputBase autocomplete="billing address-level1" size="sm" class="col-span-2" v-model="billingDetails.address" :label="$t('form.address')" />

                <InputBase autocomplete="billing postal-code" size="sm" v-model="billingDetails.postalCode" :label="$t('form.zip')" input-mode="numeric" />
                <InputBase autocomplete="billing city" size="sm" v-model="billingDetails.city" :label="$t('form.city')" />
                
                <InputSelect autocomplete="billing country" class="col-span-2" v-model="billingDetails.country" :options="regions" :label="$t('form.country')" />
              </div>
            </div>

            <div class="space-y-2" :class="[ !isBillingDetailsComplete || isLoading ? 'opacity-30 pointer-events-none' : '' ]">
              <p class="font-bold">
                {{ $t('components.Event.payment.information') }}
              </p>

              <div id="card" class="min-h-24 bg-charcoal-100 rounded-lg relative" ref="$card">
                <div class="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2">
                  <IconBase name="cute/spinner" class="spin text-charcoal-400" />
                </div>
              </div>
              <div class="flex items-center space-x-2" v-if="formData.amount > 0">
                <ButtonBase size="xs" style-type="round" color="white" @click="() => isNewCard = false" v-if="(currentUser?.squareCards ?? []).length > 0">
                  <IconBase name="cute/angle-right" class="rotate-180" :width="18" />
                </ButtonBase>
                <div class="bg-royal-200 dark:bg-royal-900 grow rounded-lg p-2 space-x-2 flex items-center cursor-pointer" @click.stop="() => isCardSave = !isCardSave">
                  <ButtonToggle class="shrink-0 !w-12" :button="{ size: '4xs' }" :model-value="isCardSave" :options="[
                    { button: { color: 'weak' }, background: 'bg-white', value: false },
                    { button: { color: 'white' }, background: 'bg-royal-600', value: true },
                  ]" />
                  <p class="text-royal-600 dark:text-royal-200 text-sm grow">
                    {{ $t('components.Event.payment.save') }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-2" :class="[ isLoading ? 'opacity-30 pointer-events-none' : '' ]" v-if="!isNewCard && formData.amount > 0">
            <div
              class="rounded-lg px-3 py-1.5 font-bold border border-whale-200 bg-charcoal-100 aspect-video flex items-end bg-cover text-whale-950 relative"
              :class="[ cardSelected?.id === card.id ? '' : 'opacity-50' ]"
              :style="{ backgroundImage: `url(${gradient})` }"
              v-for="card in currentUser?.squareCards"
              @click="cardSelected = card"
            >
              <IconBase name="mastercard" class="absolute top-1.5 left-1.5" :width="25" v-if="card.brand === 'MASTERCARD'" />
              <IconBase name="visa" class="absolute top-2.5 left-2.5" :width="28" v-if="card.brand === 'VISA'" />

              <div class="absolute bottom-1.5 right-1.5 size-5 flex items-center justify-center rounded-full bg-royal-700 text-white" v-if="cardSelected?.id === card.id">
                <IconBase name="cute/check-fill" :width="10" />
              </div>

              <ButtonBase color="white" size="2xs" style-type="round" class="!absolute top-1.5 right-1.5" @click.stop.prevent="() => onCardRemove(card.id)" :is-loading="isLoadingDeleting">
                <IconBase name="cute/trash" :width="15" />
              </ButtonBase>

              <p class="whitespace-nowrap text-2xs">{{ card.bin.slice(0, 4) }} {{ card.bin.slice(4, 6) }}** ***</p>
            </div>
            <div
              class="rounded-lg p-3 border border-charcoal-200 bg-charcoal-100 dark:bg-dark-600 dark:border-dark-500 aspect-video flex items-center justify-center text-whale-950 relative"
              @click="isNewCard = true"
            >
              <IconBase name="cute/plus-fill" class="text-charcoal-300" />
            </div>
          </div>
        </template>
      </div>

      <div class="bg-ruby-100 p-4 rounded-md text-ruby-950 font-medium" v-if="isFullPage && error">
        {{ $t('components.Event.payment.error') }}
      </div>
    </div>

    <div class="bg-white border-t border-charcoal-200 dark:bg-dark-800 dark:border-dark-600 flex items-center space-x-2 p-2.5">
      <ButtonBase style-type="round" class="shrink-0" color="white" @click="() => emit('previous')" v-if="!isFullPage">
        <IconBase name="cute/angle-right" class="rotate-180" :width="20" />
      </ButtonBase>

      <ButtonBase color="base" is-full :is-loading="isLoading" :is-disabled="!isReady || (!cardSelected && !isNewCard)" type="submit">
        {{ $t('components.Event.payment.pay', formData.amount) }}
      </ButtonBase>
    </div>
  </form>
</template>

<script lang="ts" setup>
import UserStore from '@/store/user'
import EventsStore from '@/store/events'
import gradient from '@/assets/img/gradients/hard/gradient-10.jpg'
import { Event, IEvent, Result, UserCard } from '@/classes'
import { Err } from '@/classes'
import { transactionsCardRemoveAPI } from '@/api/transactions'
import countriesFR from 'i18n-iso-countries/langs/fr.json'
import InputSelect from '@/components/Input/InputSelect.vue'

const props = defineProps<{
  event: Event
  isFullPage?: boolean
  token?: string
  formData: {
    products: { [key: string]: number }
    amount: number
  }
  isPending?: boolean
}>()

const { event } = toRefs(props)

const emit = defineEmits<{
  (e: 'next'): void
  (e: 'refresh'): void
  (e: 'error', v: Err): void
  (e: 'previous'): void
}>()

const eventStore = EventsStore()
const userStore = UserStore()
const { currentUser } = storeToRefs(userStore)

const billingDetails = reactive({
  firstName: currentUser.value?.name,
  lastName: '',
  address: '',
  city: '',
  postalCode: '',
  country: 'FR',
})

const isBillingDetailsComplete = computed(() => {
  return Object.values(billingDetails).every((v) => v)
})

const isNewCard = ref((currentUser.value?.squareCards ?? []).length === 0)
const isCardSave = ref(true)
const cardSelected: Ref<UserCard | null> = ref((currentUser.value?.squareCards ?? [])[0] ?? null)

watch(cardSelected, () => {
  if (cardSelected.value?.billing?.firstName) billingDetails.firstName = cardSelected.value.billing.firstName
  if (cardSelected.value?.billing?.lastName) billingDetails.lastName = cardSelected.value.billing.lastName
  if (cardSelected.value?.billing?.address) billingDetails.address = cardSelected.value.billing.address
  if (cardSelected.value?.billing?.city) billingDetails.city = cardSelected.value.billing.city
  if (cardSelected.value?.billing?.postalCode) billingDetails.postalCode = cardSelected.value.billing.postalCode
  if (cardSelected.value?.billing?.country) billingDetails.country = cardSelected.value.billing.country
}, { immediate: true })

const $card = ref()
const square = ref()
let squareElements: any = null

const regions = computed(() => Object.entries(countriesFR.countries).map(([ code, label ]) => {
  
  return {
    label: label,
    value: code
  }
}))

const isLoading = ref(false)
const isLoadingDeleting = ref(false)
const isReady = ref(false)

const onCardRemove = async (cardId: string) => {
  try {
    isLoadingDeleting.value = true

    await transactionsCardRemoveAPI(cardId)
    await userStore.initUser(true)
    
    if (currentUser.value?.squareCards?.length === 0) isNewCard.value = true
  } catch (e) {
    console.error(e)

    isLoading.value = false
  }

  isLoadingDeleting.value = false
}

const tokenize = async (paymentMethod: any) => {
  const tokenResult = await paymentMethod.tokenize()

  if (tokenResult.status === 'OK') {
    return tokenResult.token
  } else {
    let errorMessage = `Tokenization failed with status: ${tokenResult.status}`
    
    if (tokenResult.errors) {
      errorMessage += ` and errors: ${JSON.stringify(tokenResult.errors)}`
    }

    throw new Error(errorMessage)
  }
}

const error: Ref<Err | null> = ref(null)

const onSubmit = async () => {
  try {
    if (isNewCard.value) cardSelected.value = null
    
    isLoading.value = true

    const data: any = {
      eventId: event.value._id,
      authToken: props.token,
      products: props.formData.products,
      billing: billingDetails,
      locationId: useRuntimeConfig().public.SQUARE_LOCATION_ID
    }

    if (props.formData.amount > 0) {
      data.isCardSave = isCardSave.value

      const verificationDetails = {
        amount: props.formData.amount.toString() + '.00',
        currencyCode: 'EUR',
        billingContact: {
          givenName: billingDetails.firstName,
          familyName: billingDetails.lastName,
          countryCode: billingDetails.country,
          addressLines: [billingDetails.address],
          postalCode: billingDetails.postalCode,
          city: billingDetails.city
        },
        intent: cardSelected.value ? 'CHARGE' : (isCardSave.value ? 'CHARGE_AND_STORE' : 'CHARGE')
      }

      if (cardSelected.value) {
        data.cardId = cardSelected.value.id
      } else {
        data.token = await tokenize(squareElements)
      }
      
      const verificationResults = await square.value.verifyBuyer(
        data.token ?? data.cardId,
        verificationDetails,
      )

      data.verification = verificationResults?.token
    }
    
    const response: Result<{ event: IEvent, success: boolean }> = await fetchAPI('event/checkout', data, { method: 'POST' })

    if (response.ok && response.data.success) {
      if (response.data.event) eventStore.setItem(response.data.event, { priority: 5 })
      
      await userStore.initUser(true)
     
      emit('next')
    } else {
      console.error(response)
      
      error.value = { error: 'error' } as Err

      emit('error', { error: 'api-error' } as Err)
      emit('refresh')
      emit('previous')
    }
  } catch (e) {
    console.error(e)
    error.value = { error: 'error' } as Err
  }

  isLoading.value = false
}

watch(isNewCard, async () => {
  if (props.formData.amount === 0) {
    onSubmit()
  } else {
    try { 
      isReady.value = false

      if (!square.value) {
        square.value = await (window as any).Square.payments(
          useRuntimeConfig().public.SQUARE_APP_ID,
          useRuntimeConfig().public.SQUARE_LOCATION_ID,
        )
      }
      
      if (isNewCard.value) {
        cardSelected.value = null

        if (!squareElements) {
          setTimeout(async () => {
            squareElements = await square.value.card()
            await squareElements.attach($card.value)
          }, 100)
        }
      } else if (!cardSelected.value) {
        cardSelected.value = (currentUser.value?.squareCards ?? [])[0]
      } 

      isReady.value = true
    } catch (e) {
      console.error(e)

      emit('error', { error: 'api-error'} as Err)
      emit('previous')
    }
  }
}, { immediate: true })
</script>

<style>
.sq-card-message:not(.sq-visible) {
  display: none;
}
</style>
